import React from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { DobProofMasterResponse } from "src/models/DobProofMaster.model";
import {
  useGetDobProofMasterListQuery,
  useDeleteDobProofMasterByIdMutation,
  useChangeDobProofMasterStatusMutation,
} from "src/services/DobProofMasterService";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/toaster/showToast";
import DobProofMasterListing from "./DobProofMasterListing";
import { getColumns } from "src/utils/auth/getColumns";
import { AccessAction } from "src/utils/Enums/AccessAction";

const DobProofMasterListingWrapper = () => {
  const navigate = useNavigate();
  const [deleteDobProofMaster] = useDeleteDobProofMasterByIdMutation();
  const [changeStatus] = useChangeDobProofMasterStatusMutation();

  const { data, isLoading, isFetching } = useGetDobProofMasterListQuery("");

  const getActionOptions = (row: DobProofMasterResponse) => {
    return [
      {
        accessAction: AccessAction.EDIT,
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          navigate(`/dob-proof-master/${row._id}/edit`);
        },
      },
      {
        accessAction: AccessAction.DELETE,
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure you want to delete this DOB Proof Type?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteDobProofMaster(row._id).then((res: any) => {
                  if (res.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    showToast("success", res?.data?.message);
                  }
                });
              }
            },
          });
        },
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: DobProofMasterResponse) => <span>{row.name}</span>,
    },
    {
      field: "documentRequired",
      headerName: "Document Required",
      flex: "flex-[1_1_0%]",
      renderCell: (row: DobProofMasterResponse) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            row.documentRequired
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {row.documentRequired ? "Yes" : "No"}
        </span>
      ),
    },
    {
      field: "numberOfDocumentsRequired",
      headerName: "No. of Attachments",
      flex: "flex-[1_1_0%]",
      renderCell: (row: DobProofMasterResponse) => (
        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
          {row.numberOfDocumentsRequired ?? (row.documentRequired ? 1 : 0)}
        </span>
      ),
    },
    {
      field: "documentLabels",
      headerName: "Document Labels",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: DobProofMasterResponse) => (
        <span className="text-xs">
          {row.documentLabels?.length > 0
            ? row.documentLabels.join(", ")
            : "-"}
        </span>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: DobProofMasterResponse) => (
        <button
          className={`px-2 py-1 rounded text-xs font-medium cursor-pointer ${
            row.isActive
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
          onClick={() => {
            showConfirmationDialog({
              title: "Heads Up",
              text: `Are you sure you want to ${
                row.isActive ? "deactivate" : "activate"
              } this DOB Proof Type?`,
              icon: "question",
              showCancelButton: true,
              next: (result) => {
                if (result.isConfirmed) {
                  changeStatus(row._id).then((res: any) => {
                    if (res.error) {
                      showToast("error", res?.error?.data?.message);
                    } else {
                      showToast("success", res?.data?.message);
                    }
                  });
                }
              },
            });
          }}
        >
          {row.isActive ? "Active" : "Inactive"}
        </button>
      ),
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: DobProofMasterResponse) => {
        const options = getActionOptions(row);
        return <ATMMenu moduleName="DOB_PROOF_MASTER" options={options} />;
      },
    },
  ];

  return (
    <SideNavLayout>
      <DobProofMasterListing
        columns={getColumns(columns, "DOB_PROOF_MASTER")}
        rows={data?.data || []}
        isLoading={isLoading || isFetching}
      />
    </SideNavLayout>
  );
};

export default DobProofMasterListingWrapper;
